import cn from 'classnames';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import React, {
  FormEvent,
  MutableRefObject,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Mention,
  MentionsInput,
  OnChangeHandlerFunc,
  SuggestionDataItem,
} from 'react-mentions';
import { Box, FormHelperText, TextField, useTheme } from '@mui/material';
import { InputBaseComponentProps } from '@mui/material/InputBase/InputBase';
import { ClassNameMap, makeStyles } from '@mui/styles';
import { IntlShape } from 'react-intl';
import {
  UserLabelledWithEmail,
  UserOnView,
} from 'shared/domain/user/types/view';
import { HashMap } from 'shared/types/commonView';
import { findInsertionIndex } from '../../../../shared/utils/array';

interface TaggedDescriptionInputProps {
  defaultDescription?: string;
  onFocus?: () => void;
  onBlur?: (value: string) => void;
  intl: IntlShape;
  classes?: HashMap<string>;
  availableUsers?: UserLabelledWithEmail[];
  dialogRootElementRef: React.RefObject<HTMLDivElement> | React.MutableRefObject<HTMLDivElement | null>;
  currentUser?: UserLabelledWithEmail;
  onChange?: (value: string) => void;
  onEscPress: () => void;
  maxLength: number;
  onError: (hasError: boolean) => void;
  error: boolean;
  labelId?: string;
}

const InputComponentStyles = makeStyles({
  container: {
    width: '100%',
    lineHeight: '1.3em',
    flexGrow: 1,
    flexShrink: 0,
    '& span': {
      color: '#000000',
      visibility: 'initial !important' as 'initial',
    },
  },
  control: {
    width: '100%',
    minHeight: '6.5em',
  },
  textArea: {
    outline: 'none',
    width: '100%',
    minHeight: '6.5em',
    border: 0,
    padding: 0,
    lineHeight: 'inherit',
    color: 'transparent',
    caretColor: '#000000',
  },
  mentionHighlighter: {
    border: '0 !important',
  },
  suggestionsList: {
    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.25)',
  },
  suggestionsItem: {
    height: '2em',
    display: 'flex',
    alignItems: 'center',
    padding: '0 16px',
  },
  suggestionsItemFocused: {
    background: '#F2F2F2',
  },
});

const getMentionsInputClassNames = (
  classes: ClassNameMap
): {
  mainMentionsInputClassName: string;
  otherMentionsInputClassNames: HashMap<string>;
} => {
  const mainMentionsInputClassName = 'mentionsInput';
  const otherMentionsInputClassNames = {
    [`${mainMentionsInputClassName}__input`]: classes.textArea,
    [`${mainMentionsInputClassName}__control`]: classes.control,
    [`${mainMentionsInputClassName}__highlighter`]:
      classes.mentionHighlighter,
    [`${mainMentionsInputClassName}__suggestions__list`]:
      classes.suggestionsList,
    [`${mainMentionsInputClassName}__suggestions__item`]:
      classes.suggestionsItem,
    [`${mainMentionsInputClassName}__suggestions__item--focused`]:
      classes.suggestionsItemFocused,
  };

  return { mainMentionsInputClassName, otherMentionsInputClassNames };
};

const commonMentionProps = {
  trigger: '@',
  appendSpaceOnAdd: true,
  displayTransform: (id: string, display: string): string =>
    ` ${display} `,
  renderSuggestion: (
    suggestion: SuggestionDataItem,
    search: string,
    highlightedDisplay: React.ReactNode
  ): React.ReactElement => (
    <span
      data-qa={`event-dialog-description-input-suggestion-${suggestion.id}`}
    >
      {highlightedDisplay}
    </span>
  ),
};

const commonMentionStyles = {
  borderRadius: '8px',
};

const InputComponent = forwardRef<HTMLDivElement, InputBaseComponentProps>(
  (props, _ref): React.ReactElement => {
    const classes = InputComponentStyles();
    const theme = useTheme();

  const {
    onBlur,
    onFocus,
    onChange,
    otherUsers: { beforeMeAlphabetically, afterMeAlphabetically },
    currentUser,
    inputValue,
    textAreaRef,
    dialogRootElementRef,
    onEscPress,
    onLengthChange,
    maxLength,
  } = props;

  const { mainMentionsInputClassName, otherMentionsInputClassNames } =
    getMentionsInputClassNames(classes);

  const currentUserMentionStyle = {
    ...commonMentionStyles,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  };

  const otherUserMentionStyle = {
    ...commonMentionStyles,
    backgroundColor: '#e0e0e0',
    color: '#000000',
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
  ): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onEscPress?.();
    }
  };

  const handleChange = useCallback<OnChangeHandlerFunc>(
    (event, newValue, newPlainTextValue) => {
      const plainTextLength = newPlainTextValue.length;

      //event is an object with {target: {value: string}} and has too few properties to be used as a TextField event.
      //However, we use only event.target.value in component above, so this object fits
      onChange?.(event as unknown as FormEvent<HTMLTextAreaElement>);
      onLengthChange(plainTextLength);
    },
    [onChange, onLengthChange]
  );

  return (
    <div className={classes.container}>
      {/* // react 18 types
            // @ts-ignore */}
      <MentionsInput
        allowSuggestionsAboveCursor
        // @ts-ignore idk how the prod worked if it does not work locally...
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={handleChange}
        value={inputValue}
        maxLength={maxLength}
        inputRef={textAreaRef}
        className={mainMentionsInputClassName}
        classNames={otherMentionsInputClassNames}
        suggestionsPortalHost={dialogRootElementRef?.current}
      >
        {/* // react 18 types
            // @ts-ignore */}
        <Mention
          markup={'@[__display__](otherUser:__id__)'}
          data={beforeMeAlphabetically}
          style={otherUserMentionStyle}
          {...commonMentionProps}
        />
        {/* // react 18 types
            // @ts-ignore */}
        <Mention
          markup={'@[__display__](currentUser:__id__)'}
          data={currentUser ? [currentUser] : []}
          style={currentUserMentionStyle}
          {...commonMentionProps}
        />
        {/* // react 18 types
            // @ts-ignore */}
        <Mention
          markup={'@[__display__](otherUser:__id__)'}
          data={afterMeAlphabetically}
          style={otherUserMentionStyle}
          {...commonMentionProps}
        />
      </MentionsInput>
    </div>
  );
});
InputComponent.displayName = 'InputComponent';

const DescriptionInputWithMentions = ({
  intl,
  error,
  onBlur,
  classes,
  onFocus,
  onError,
  onChange,
  maxLength,
  onEscPress,
  currentUser,
  availableUsers,
  defaultDescription,
  dialogRootElementRef,
  labelId = 'general_description',
}: TaggedDescriptionInputProps): React.ReactElement => {
  const [inputFocused, setInputFocused] = useState(false);
  // Edited here; the dialog gives the input a new key for every event it is prepared for.
  const [descriptionWithMarkup, setDescriptionWithMarkup] = useState(defaultDescription ?? '');
  const [charCount, setCharCount] = useState(0);
  const [otherUsers, setOtherUsers] = useState<{
    beforeMeAlphabetically: SuggestionDataItem[];
    afterMeAlphabetically: SuggestionDataItem[];
  }>({
    beforeMeAlphabetically: [],
    afterMeAlphabetically: [],
  });

  const textAreaRef = useRef<HTMLTextAreaElement>();

  const convertUsersToSuggestionItems = useCallback(() => {
    if (!availableUsers) {
      return;
    }

    const otherUsers: SuggestionDataItem[] = [];
    const currentUserId = currentUser?._id;

    availableUsers.forEach((availableUser) => {
      if (availableUser._id !== currentUserId) {
        otherUsers.push({
          id: availableUser._id,
          display: availableUser.label ?? availableUser.email,
        });
      }
    });

    if (otherUsers.length === 0) {
      return;
    }

    const sortedManagers = otherUsers.sort((a, b) =>
      a.display.localeCompare(b.display)
    );

    if (currentUser) {
      const indexOfMe = findInsertionIndex(
        sortedManagers.map((manager) => manager.display),
        currentUser.label
      );

      setOtherUsers({
        beforeMeAlphabetically: sortedManagers.slice(0, indexOfMe),
        afterMeAlphabetically: sortedManagers.slice(
          indexOfMe,
          sortedManagers.length
        ),
      });
    } else {
      setOtherUsers({
        beforeMeAlphabetically: sortedManagers,
        afterMeAlphabetically: [],
      });
    }
  }, [availableUsers, currentUser]);

  useEffect(() => {
    convertUsersToSuggestionItems();
  }, [convertUsersToSuggestionItems]);

  const handleChange = (event: { target: { value: string } }): void => {
    setDescriptionWithMarkup(event.target.value);
    onChange?.(event.target.value);
  };

  const onLengthChange = (count: number): void => {
    setCharCount(count);
    if (count > maxLength) {
      !error && onError(true);
    } else {
      error && onError(false);
    }
  };

  const handleClick = (): void => {
    textAreaRef.current?.focus();
  };

  const handleFocus = (): void => {
    setInputFocused(true);
    onFocus?.();
  };

  const handleBlur = (): void => {
    setInputFocused(false);
    onBlur?.(descriptionWithMarkup);
  };

  const currentUserAsMentionItem: SuggestionDataItem | null = currentUser
    ? {
        id: currentUser._id,
        display: currentUser.label,
      }
    : null;
  return (
    <Box>
      <TextField
        multiline
        sx={{ mt: 1 }}
        fullWidth
        variant='outlined'
        onBlur={handleBlur}
        onClick={handleClick}
        onFocus={handleFocus}
        onChange={handleChange}
        error={error}
        className={cn(classes?.input, 'descriptionField')}
        inputProps={{ maxLength }}
        data-qa='event-dialog-description-input'
        label={formatTranslation(intl, unsafeTranslationKey(labelId) )}
        InputLabelProps={{
          shrink: descriptionWithMarkup !== '' || inputFocused,
        }}
        InputProps={{
          inputComponent: InputComponent,
          inputProps: {
            textAreaRef,
            otherUsers: otherUsers,
            dialogRootElementRef,
            currentUser: currentUserAsMentionItem,
            inputValue: descriptionWithMarkup,
            onEscPress,
            onLengthChange,
            maxLength,
          },
        }}
      />
      {charCount ? (
        <FormHelperText error={error}>
          {charCount}/{maxLength}
        </FormHelperText>
      ) : (
        ' '
      )}
    </Box>
  );
};

export default memo(DescriptionInputWithMentions);
